import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Mail, MapPin, Phone } from "lucide-react";
import { api } from "@/lib/api";
import { inr, mapLink, telHref } from "@/lib/format";
import useSeo from "@/hooks/useSeo";
import RoomCard from "@/components/RoomCard";
import MapEmbed from "@/components/MapEmbed";
import { Reveal } from "@/components/Reveal";
import NotFound from "@/pages/NotFound";

const Gallery = ({ images, name }) => (
  <section className="container-x py-16">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="property-gallery">
      {images.slice(1).map((src, i) => (
        <div key={src} className={`overflow-hidden ${i === 0 ? "col-span-2 row-span-2 aspect-square md:aspect-auto" : "aspect-[4/3]"}`}>
          <img src={src} alt={`${name} photo ${i + 2}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
      ))}
    </div>
  </section>
);

export default function PropertyPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setP(null);
    api.get(`/properties/${slug}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [slug]);
  useSeo(p ? `${p.name} - Rooms, Rates & Location` : "Hotel", p ? `${p.name}, ${p.city}, ${p.state}. ${p.tagline}. Room categories: ${p.rooms.map((r) => r.name).join(", ")}.` : undefined);

  if (missing) return <NotFound />;
  if (!p) return <div className="min-h-screen bg-[#1E3B2E]" />;
  const bookable = !p.coming_soon && p.rooms.length > 0;

  return (
    <>
      <section className="relative h-[85svh] min-h-[520px] flex items-end overflow-hidden bg-[#1E3B2E]">
        {p.images[0] && <img src={p.images[0]} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />}
        <div className="absolute inset-0 hero-shade" />
        <div className="relative container-x pb-14 text-[#FDFBF7]">
          <p className="eyebrow text-[#E5D9C5] flex items-center gap-2"><MapPin size={13} />{p.city}, {p.state}</p>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-3xl" data-testid="property-name">{p.name}</h1>
          <p className="mt-4 text-lg opacity-85">{p.tagline}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            {bookable ? <Link to={`/book?property=${p.slug}`} className="btn-gold !h-14 !px-10" data-testid="property-book-now-btn">Book Now</Link>
              : <a href={telHref(p.phone)} className="btn-gold !h-14 !px-10" data-testid="property-call-btn">Call to book</a>}
          </div>
        </div>
      </section>

      {!p.coming_soon && (
        <section className="container-x pt-20 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow text-[#C28B5E]">About the property</p>
            <p className="mt-5 text-lg leading-9 text-[#3b3b3b] font-light" data-testid="property-description">{p.description}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <p className="eyebrow text-[#C28B5E]">Amenities</p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {p.amenities.map((a) => <li key={a} className="flex items-start gap-2 text-sm"><Check size={14} className="mt-1 text-[#C28B5E] shrink-0" />{a}</li>)}
            </ul>
          </Reveal>
        </section>
      )}

      {p.images.length > 1 && <Gallery images={p.images} name={p.name} />}

      {p.rooms.length > 0 && (
        <section id="rooms" className="bg-[#F4EFE6] py-20">
          <div className="container-x">
            <p className="eyebrow text-[#C28B5E]">Accommodation</p>
            <h2 className="mt-3 text-4xl sm:text-5xl text-[#1E3B2E] tracking-tight">Room categories</h2>
            {!p.rooms.some((r) => r.price) && <p className="mt-4 text-sm text-[#3b3b3b]" data-testid="property-rates-note">{p.listed_from_price ? <>Rates from <strong className="font-['Cormorant_Garamond'] text-2xl text-[#1A1A1A]">{inr(p.listed_from_price)}</strong> / night (starting rate published on anantahotels.com). </> : ""}Book online now: the hotel confirms your exact room rate before you pay.</p>}
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {p.rooms.map((r) => <RoomCard key={r.slug} room={r} propertySlug={p.slug} fallbackImage={null} />)}
            </div>
          </div>
        </section>
      )}

      {!p.coming_soon && (
        <section className="container-x py-20 grid lg:grid-cols-3 gap-10">
          <div className="space-y-4 text-sm" data-testid="property-contact">
            <p className="eyebrow text-[#C28B5E]">Contact &amp; location</p>
            <p className="flex gap-3"><MapPin size={16} className="mt-0.5 shrink-0" />{p.address}</p>
            <a href={telHref(p.phone)} className="flex gap-3 hover:text-[#C28B5E]"><Phone size={16} />{p.phone}</a>
            <a href={`mailto:${p.email}`} className="flex gap-3 hover:text-[#C28B5E] break-all"><Mail size={16} className="shrink-0" />{p.email}</a>
            <a href={mapLink(p.map_query)} target="_blank" rel="noopener noreferrer" className="btn-ghost text-[#1E3B2E] hover:bg-[#1E3B2E] hover:text-white" data-testid="property-directions-btn">Get directions</a>
          </div>
          <div className="lg:col-span-2"><MapEmbed query={p.map_query} title={`${p.name} map`} /></div>
        </section>
      )}
    </>
  );
}
